import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isEqual, unset } from 'lodash/fp'
import set from 'lodash/fp/set'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'

import { userActivityEvents } from 'analytics/utils'
import { toast } from 'components/ui/toaster'
import { useQueryWithMock } from 'dummy/static'
import useEcpTranslation from 'hooks/common/useEcpTranslation'
import { ContractDetails } from 'utils/ContractUtils'
import EntityUtils from 'utils/EntityUtils'
import { useIsFeatureFlagEnabled } from 'utils/FeatureFlagUtils'
import { RepeatablePaymentOption } from 'utils/PaymentUtils'

import { EntityItem, PortalUser } from '../api/ApiTypes'
import { getCustomerApiClient } from '../api/CustomerApiClient'
import { CUSTOMER_QUERY_IDS } from '../api/Queries'
import AttributeUtils from '../utils/AttributeUtils'
import { Locale } from '../utils/LanguageUtils'
import { Address } from '../utils/OrderUtils'

import { useAnalytics } from './AnalyticsContext'
import AuthContext from './AuthContext'
import PortalConfigContext, { EntityAction } from './PortalConfigContext'

export type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>
}

export interface PendingChange {
  new_value: any
  requested_at: string
  requested_by: 'ecp'
}

export enum RepeatableAttribute {
  email = 'email',
  phone = 'phone',
  address = 'address',
  payment = 'payment',
  payment_method = 'payment_method'
}

export interface RepeatableEmail {
  _id?: string
  id?: string
  _tags?: string[]
  email: string
}

export interface RepeatablePhone {
  _id?: string
  id?: string
  _tags?: string[]
  phone: string
}

export interface RepeatableAddress extends Address {
  id?: string
}

export type RepeatableAttributeItem =
  | RepeatableEmail
  | RepeatablePhone
  | RepeatableAddress
  | RepeatablePaymentOption

export enum ConsentStatus {
  Active = 'active',
  Pending = 'pending',
  Declined = 'declined',
  Unknown = 'unknown'
}

export interface ConsentIdentifier {
  events?: unknown[]
  identifier: string
  status: ConsentStatus
}

export interface Consent {
  status: ConsentStatus
  identifiers: ConsentIdentifier[]
}

export interface Contact {
  first_name: string
  last_name: string
  name?: string
  email: RepeatableEmail[]
  address: RepeatableAddress[]
  _schema: string
  _id: string
  _org: string
  _created_at: string
  _title: string
  language: Locale
  phone: RepeatablePhone[]
  birthdate?: string
  _updated_at: string
  payment?: RepeatablePaymentOption[]
  salutation?: string
  consent_email_marketing?: Consent
  consent_phone_call?: Consent
  consent_print_marketing?: Consent
  consent_sms_marketing?: Consent
  entity?: EntityItem
  files?: EntityItem[]
  relations?: EntityItem[]
  journey_actions?: EntityAction[]
  _pending_changes?: any
  first_ecp_login?: string
  first_installer_portal_login?: string
}

interface ContactContext {
  data: {
    contact: Contact
    isDirty: boolean
    isFetching: boolean
    isFetchingContact: boolean
    isFetchingPortalUser: boolean
    isUpdating: boolean
    isError: boolean
    isEditMode: boolean
    selectedAddress: RepeatableAddress
    portalUser: PortalUser
    relations: EntityItem[]
  }
  actions: {
    saveContact: (groupName: string) => void
    updateContact: (
      partialContact: DeepPartial<unknown>,
      attributeName?: string
    ) => void
    updateContactAndSave: (
      partialContact: DeepPartial<Contact>,
      onSuccessFn: any
    ) => void
    resetContact: () => void
    setIsEditMode: (isEditMode: boolean) => void
    updateEditMode: (isEditMode: boolean) => void
    setSelectedAddress: (address: RepeatableAddress) => void
  }
}

const defaultContext: ContactContext = {
  data: {
    contact: null,
    isDirty: false,
    isFetching: false,
    isFetchingContact: false,
    isFetchingPortalUser: false,
    isUpdating: false,
    isError: false,
    isEditMode: false,
    selectedAddress: null,
    portalUser: null,
    relations: null
  },
  actions: {
    updateContact: () => null,
    saveContact: () => null,
    updateContactAndSave: () => null,
    resetContact: () => null,
    setIsEditMode: () => false,
    updateEditMode: () => false,
    setSelectedAddress: () => null
  }
}

const ContactContext = createContext<ContactContext>(defaultContext)

interface Props {
  children: ReactNode
}

const ContactContextProvider = ({ children }: Props) => {
  const queryClient = useQueryClient()
  const { t } = useEcpTranslation()
  const { data: authData } = useContext(AuthContext)
  const [isEditMode, setIsEditMode] = useState(false)
  const [contact, setContact] = useState<Contact>(undefined)
  const [portalUser, setPortalUser] = useState<PortalUser>(null)
  const [relations, setRelations] = useState<EntityItem[]>(null)
  const [originalState, setOriginalState] = useState<Contact>(null)
  const { data: portalConfigData } = useContext(PortalConfigContext)
  const isApprovalStateEnabled = useIsFeatureFlagEnabled('approvalState')
  const { analytics } = useAnalytics()
  const [selectedAddress, setSelectedAddress] =
    useState<RepeatableAddress>(null)

  const contactQuery = useQueryWithMock({
    queryKey: [
      CUSTOMER_QUERY_IDS.GET_CONTACT,
      authData?.user?.attributes['custom:contact_entity_id'],
      'details'
    ],
    queryFn: () => getCustomerApiClient().getContact(),
    // Only fetch the contact when the user is actually mapped to one. Users
    // who registered without a Contact have no `custom:contact_entity_id`;
    // calling getContact() for them fails and would surface as a fatal
    // "Failed to load essential portal data" error, instead of letting the
    // routing layer redirect them to the pending account page.
    enabled: Boolean(authData?.user?.attributes?.['custom:contact_entity_id']),
    onSuccess: (data: ContractDetails) => {
      const mappedContact = EntityUtils.mapContactWithRepeatableIds(
        data?.entity as Contact
      )

      const mappedContactWithFiles = {
        ...mappedContact,
        journey_actions: data?.journey_actions,
        files: data?.files
      }

      setIsEditMode(false)
      setOriginalState(mappedContactWithFiles)
      setContact(mappedContactWithFiles)
      setRelations(data?.relations)
    }
  })

  const portalUserQuery = useQueryWithMock({
    queryKey: [
      CUSTOMER_QUERY_IDS.PORTAL_USER,
      authData?.user?.attributes['custom:portal_user_id'],
      'details'
    ],
    queryFn: () => getCustomerApiClient().getPortalUser(),
    enabled: Boolean(authData.user && contact?._id),
    onSuccess: (data: any) => {
      setPortalUser(data?.data)
    }
  })

  const isDirty = useMemo(() => {
    if (!contact) {
      return false
    }

    const clonedContact = { ...contact }
    const clonedOriginalState = { ...originalState }

    /*
     delete files before comparison, because
     we do not have separate save button for files,
     and we do not to show unsaved dialog for them
     */
    delete clonedContact.files
    delete clonedOriginalState.files

    return !isEqual(
      EntityUtils.unmapContactOfRepeatableIds(clonedContact),
      EntityUtils.unmapContactOfRepeatableIds(clonedOriginalState)
    )
  }, [contact, originalState])

  const updateMutation = useMutation(
    (contact: Contact) => getCustomerApiClient().updateContact(null, contact),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          CUSTOMER_QUERY_IDS.GET_CONTACT,
          contact?._id
        ])
      },
      onError: (error: any) => {
        let message = error?.response?.data?.error

        if (error?.response?.status === 500) {
          message = t('common.error.failed_update_attribute')
        }

        if (error?.response?.status === 403) {
          message = t('common.error.not_permitted')
        }

        toast.error({
          title: t('common.error.something_went_wrong'),
          description: message
        })
      }
    }
  )

  const saveContact = (groupName: string): void => {
    const contactWithUnmappedRepeatableIds =
      EntityUtils.unmapContactOfRepeatableIds(contact)

    updateMutation.mutate(contactWithUnmappedRepeatableIds, {
      onSuccess: () => {
        analytics?.addEvents([
          {
            type: userActivityEvents.additionalInfoUpdated,
            details: {
              pagePath: window.location.pathname,
              entityId: contact?._id,
              entityName: contact?._title,
              entitySchema: contact?._schema,
              group: groupName
            },
            timestamp: new Date().toISOString()
          }
        ])
      }
    })
  }

  const updateContact = useCallback(
    (
      partialContact: DeepPartial<Contact> | RepeatableAttributeItem,
      attributeName: string
    ) => {
      setContact((oldContact) => {
        // if it's a pending approval attribute
        if (
          AttributeUtils.needsApproval(
            attributeName,
            'contact',
            portalConfigData?.portalConfig?.approval_state_attributes
          ) &&
          isApprovalStateEnabled
        ) {
          const differentFromOld = partialContact !== oldContact[attributeName]

          if (differentFromOld) {
            const incomingPendingChanges: PendingChange = {
              new_value: partialContact,
              requested_at: new Date(Date.now()).toISOString(),
              requested_by: 'ecp'
            }

            return set(
              `_pending_changes.${attributeName}`,
              incomingPendingChanges,
              oldContact
            )
          } else {
            return unset(`_pending_changes.${attributeName}`, oldContact)
          }
        }

        return set(attributeName, partialContact, oldContact) as Contact
      })
    },
    [
      isApprovalStateEnabled,
      portalConfigData?.portalConfig?.approval_state_attributes
    ]
  )

  const updateContactAndSave = (
    partialContact: DeepPartial<Contact>,
    onSuccess: any
  ) => {
    const updatedContact = { ...contact, ...partialContact } as Contact

    setContact(updatedContact)

    updateMutation.mutate(updatedContact, { onSuccess })
  }

  const resetContact = (): void => {
    setContact(originalState)
  }

  const updateEditMode = (isEditMode: boolean) => {
    setIsEditMode(isEditMode)
  }

  return (
    <ContactContext.Provider
      value={{
        data: {
          contact,
          isDirty,
          isFetching:
            contactQuery.isInitialLoading && portalUserQuery.isInitialLoading,
          isFetchingContact: contactQuery.isInitialLoading,
          isFetchingPortalUser: portalUserQuery.isInitialLoading,
          isUpdating: updateMutation.isLoading,
          isError: contactQuery.isError || portalUserQuery.isError,
          selectedAddress,
          isEditMode,
          portalUser,
          relations
        },
        actions: {
          saveContact,
          updateContact,
          updateContactAndSave,
          resetContact,
          setIsEditMode,
          updateEditMode,
          setSelectedAddress
        }
      }}
    >
      {children}
    </ContactContext.Provider>
  )
}

export { ContactContextProvider, ContactContext }
